import { useEffect, useMemo, useState } from 'react';
import { MassIncidentIcon, ticketStatusLabel } from '@/shared/config';
import { apiErrorText, ticketsApi } from '@/shared/api';
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  ErrorState,
  Field,
  Scroller,
  Select,
  Skeleton,
  StatusBadge,
  Textarea,
  toast,
} from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { pluralRu } from '@/shared/lib/plural';
import { replyRecipients } from '../model/logic';
import { refreshAfterIncidentChange } from '../model/store';
import { isLiveTicket, useIncident } from '../model/useIncident';
import styles from './MassIncidents.module.css';

const STATUS_OPTIONS = [
  { value: '', label: 'Не менять статус' },
  { value: 'in_progress', label: ticketStatusLabel('in_progress') },
  { value: 'waiting_for_requester', label: ticketStatusLabel('waiting_for_requester') },
  { value: 'resolved', label: ticketStatusLabel('resolved') },
  { value: 'closed', label: ticketStatusLabel('closed') },
];

export const MassReplyDialog: React.FC<{
  incidentId: string;
  sourceTicketId?: string;
  onClose: () => void;
}> = ({ incidentId, sourceTicketId, onClose }) => {
  const { summary, tickets, loading, error, reload } = useIncident(incidentId);
  const live = useMemo(() => tickets.filter(isLiveTicket), [tickets]);
  const [checked, setChecked] = useState<Set<string> | null>(null);
  const [body, setBody] = useState('');
  const [status, setStatus] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (checked === null && live.length > 0) setChecked(new Set(live.map((t) => t.id)));
  }, [live, checked]);

  const selected = checked ?? new Set<string>();
  const toggle = (id: string, on: boolean) =>
    setChecked((prev) => {
      const next = new Set(prev ?? []);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const allOn = live.length > 0 && live.every((t) => selected.has(t.id));

  const send = async () => {
    const { ticketIds: recipients, all } = replyRecipients(live.map((t) => t.id), selected);
    const source = live.find((t) => t.id === sourceTicketId)?.id ?? live[0]?.id;
    if (!body.trim() || recipients.length === 0 || !source) return;
    setSending(true);
    try {
      const res = await ticketsApi.replyMassIncident(
        source,
        body.trim(),
        status || undefined,
        all ? undefined : recipients,
      );
      const r = res.data;
      const parts = [`отправлено в ${r.messaged} из ${r.total}`];
      if (r.assigned) parts.push(`назначены вам: ${r.assigned}`);
      if (status) parts.push(`статус сменён у ${r.statusChanged}`);
      if (r.skipped.length) parts.push(`пропущено ${r.skipped.length}`);
      toast.success(`Массовый ответ: ${parts.join(', ')}`);
      refreshAfterIncidentChange(incidentId, recipients);
      onClose();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось отправить массовый ответ'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} dirty={body.trim().length > 0}>
      <DialogContent className={styles.wide}>
        <DialogTitle>Ответить всем в сбое</DialogTitle>
        <DialogDescription>
          {summary ? `«${summary.title}». ` : ''}Сообщение уйдёт в чат каждой отмеченной заявки, авторы
          получат уведомление, вы станете исполнителем.
        </DialogDescription>

        <div className={styles.replyForm}>
          <Field label="Текст ответа">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Например: проблема со связью на 5 этаже устранена, проверьте доступ."
              rows={4}
              maxLength={10000}
            />
          </Field>
          <Field label="Сменить статус отмеченных" floated>
            <Select
              aria-label="Статус для массовой смены"
              value={status}
              onValueChange={setStatus}
              options={STATUS_OPTIONS}
            />
          </Field>

          <div className={styles.recipientsHead}>
            <span>
              Получатели · {selected.size} из {live.length}
            </span>
            {live.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setChecked(allOn ? new Set() : new Set(live.map((t) => t.id)))}
              >
                {allOn ? 'Снять все' : 'Отметить все'}
              </Button>
            )}
          </div>
          {error ? (
            <ErrorState message={error} onRetry={() => void reload()} />
          ) : loading && tickets.length === 0 ? (
            <div className={styles.rows}>
              {[0, 1, 2].map((i) => (
                <div key={i} className={styles.recipient}>
                  <Skeleton width="1.125rem" height="1.125rem" radius="var(--radius-sm)" />
                  <Skeleton width="70%" height="0.875rem" />
                </div>
              ))}
            </div>
          ) : (
            <Scroller className={styles.recipientsScroller} viewportClassName={styles.rows}>
              {tickets.map((t) => {
                const closed = !isLiveTicket(t);
                return (
                  <label key={t.id} className={cn(styles.recipient, closed && styles.rowClosed)}>
                    <Checkbox
                      checked={!closed && selected.has(t.id)}
                      disabled={closed}
                      onCheckedChange={(on) => toggle(t.id, on)}
                      aria-label={`№${t.number ?? ''} ${t.title}`}
                    />
                    <span className={styles.rowNumber}>{t.number != null ? `№${t.number}` : ''}</span>
                    <span className={styles.rowTitle}>{t.title}</span>
                    {closed ? (
                      <span className={styles.rowPerson}>закрыта — пропустим</span>
                    ) : (
                      <StatusBadge status={t.status} label={t.statusLabel} size="sm" />
                    )}
                  </label>
                );
              })}
            </Scroller>
          )}
        </div>

        <DialogFooter>
          <DialogClose render={<Button variant="ghost">Отмена</Button>} />
          <Button onClick={() => void send()} disabled={sending || !body.trim() || selected.size === 0}>
            <MassIncidentIcon size={14} aria-hidden />
            {sending
              ? 'Отправка…'
              : `Отправить ${selected.size} ${pluralRu(selected.size, 'заявке', 'заявкам', 'заявкам')}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
